import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import OrgManager from '../services/OrgManager';
import { IS_REPORTS_MODE } from '../config';

const OrgContext = createContext(null);

/**
 * OrgProvider — wraps the app root and provides org context to all children.
 *
 * mode:
 *   'org'       — specific org is active; dashboards scoped to that org
 *   'all'       — merged view across all orgs the user belongs to
 *
 * Users must be added to an org by an admin. If a user has no org memberships,
 * the app shows no org context.
 */
export function OrgProvider({ children }) {
  const [orgs, setOrgs] = useState([]);            // all orgs user belongs to
  const [activeOrg, setActiveOrg] = useState(null); // { id, name, slug, my_role, ... }
  const [mode, setMode] = useState('org');           // 'org' | 'all'
  const [loading, setLoading] = useState(true);
  const [orgChangeKey, setOrgChangeKey] = useState(0); // increments on every org switch

  const loadOrgs = useCallback(async () => {
    try {
      const data = await OrgManager.listMyOrgs();
      setOrgs(data || []);

      const savedOrgId = await AsyncStorage.getItem('active_org_id');
      const savedMode  = await AsyncStorage.getItem('org_mode');

      if (savedMode === 'all' && (data || []).length > 1) {
        setMode('all');
        setActiveOrg(null);
      } else {
        const all = data || [];
        const found =
          all.find(o => o.id === savedOrgId) ||
          all.find(o => !o.is_personal) ||
          all[0] ||
          null;
        if (found) {
          setActiveOrg(found);
          setMode('org');
          await AsyncStorage.setItem('active_org_id', found.id);
          await AsyncStorage.setItem('org_mode', 'org');
        }
      }
    } catch (err) {
      console.warn('OrgProvider: failed to load orgs', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // The standalone report viewer (reports.* / /r/:token) is login-free: there is no
    // Firebase user, so listMyOrgs() would just fail with a 401/404. Skip it there.
    if (IS_REPORTS_MODE) {
      setLoading(false);
      return;
    }
    loadOrgs();
  }, [loadOrgs]);

  const switchOrg = useCallback(async (orgId) => {
    if (!orgId) {
      // Switching to null is not supported — ignore silently.
      console.warn('OrgProvider: switchOrg(null) is not supported.');
      return;
    }
    const found = orgs.find(o => o.id === orgId);
    if (found) {
      setActiveOrg(found);
      setMode('org');
      setOrgChangeKey(k => k + 1);
      await AsyncStorage.setItem('active_org_id', orgId);
      await AsyncStorage.setItem('org_mode', 'org');
    }
  }, [orgs]);

  const setAllOrgsMode = useCallback(async () => {
    setActiveOrg(null);
    setMode('all');
    setOrgChangeKey(k => k + 1);
    await AsyncStorage.setItem('active_org_id', '');
    await AsyncStorage.setItem('org_mode', 'all');
  }, []);

  const value = {
    orgs,
    activeOrg,
    mode,
    loading,
    orgChangeKey,
    isOrgMode: mode === 'org',
    isAllOrgsMode: mode === 'all',
    myRole: activeOrg?.my_role || null,
    switchOrg,
    setAllOrgsMode,
    refreshOrgs: loadOrgs,
  };

  if (loading) return null;

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg() {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used inside OrgProvider');
  return ctx;
}
